import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedNumber } from 'react-intl';
import actionCreator from '../../redux/offer/action-creator';
import offerStateSelectors from '../../redux/offer/state-selectors';
import stateSelectors from '../../redux/state-selectors';

import {
    Box,
    Card,
    Stack,
    Type,
} from 'react-spark';
import { offerSchema, offersSchemaGroups } from '../../offers-page-schemas';
import { formatMoney, sortNumbersAsc, sortNumbersDesc } from '../../../../utils/numbers';
import { QuestionMarkIcon } from '../../icons';
import Button from '../../button/Button';
import Tooltip from '../../tooltip/Tooltip';
import StarIcon from '../../../containers/page/StarIcon';

class OfferV2ListRowComponent extends Component {
    static propTypes = {
        offer: offerSchema.isRequired,
        numberOfTerms: PropTypes.number.isRequired,
        isSelectingOffer: PropTypes.bool.isRequired,
        selectOffer: PropTypes.func.isRequired,
        hasQlDiscount: PropTypes.bool.isRequired,
        qlDiscountAmount: PropTypes.string, // can be undefined
        autoPayDiscountConfig: PropTypes.object
    };

    render() {
        const { isSelectingOffer, numberOfTerms, offer, selectOffer, hasQlDiscount, qlDiscountAmount, autoPayDiscountConfig } = this.props;
        const {
            id,
            monthlyPayment,
            duration,
            annualPercentageRate,
            rate: autoPayRate,
            originationFee,
            originationFeeRate,
            amountFinanced,
        } = offer;
        const isOnlyOffer = numberOfTerms === 1;
        // defaulting to present behavior, keeping dynamic for existing loans
        let autoPayDiscountAmount = 0.25;
        let autoPayDiscountAppliesToRate = false;
        let autoPayDiscountAppliesToApr = true;
        if (autoPayDiscountConfig) {
            autoPayDiscountAmount = autoPayDiscountConfig.discountAmount || autoPayDiscountAmount;
            const appliesTo = String(autoPayDiscountConfig.appliesTo).toUpperCase();
            autoPayDiscountAppliesToRate = appliesTo.includes('RATE');
            autoPayDiscountAppliesToApr = appliesTo.includes('APR');
        }

        const rateAprAdditionalClass = hasQlDiscount ? 'discount-text' : '';
        let rateAdditionalTooltip = '';
        if (autoPayDiscountAppliesToRate) {
            rateAdditionalTooltip = `Enrolling in autopay reduces your rate by ${autoPayDiscountAmount}%.`;
        }
        const aprAdditionalTooltip = autoPayDiscountAppliesToApr ? ` Enrolling in autopay reduces your APR by ${autoPayDiscountAmount}%.` : '';
        const aprTooltip = `APR (Annual Percentage Rate) represents the total annual cost of your loan, including your interest rate and origination fee. These numbers are estimates.${aprAdditionalTooltip}`

        return (
            <Box className="sprk-u-phm" style={{ flex: 'auto' }}>
                <Stack className={`sprk-u-pbs sprk-u-Flex--col ${isOnlyOffer ? 'sprk-u-Flex--row@s' : 'sprk-u-Flex--row@m'
                    }`} style={{ gap: '12px' }}>
                    <Stack.Item className='sprk-u-man' style={{ flex: 4 }}>
                        <Stack className={`sprk-u-Flex--row ${isOnlyOffer ? 'sprk-u-Flex--col@s sprk-u-Flex--row@m' : ''
                            }`} style={{ gap: '12px' }}>
                            <Stack.Item style={{ flex: 1 }}>
                                <Type.BodyFour
                                    className="antialiased sprk-u-Color--black-tint-75 sprk-u-WhiteSpace--nowrap">
                                    Monthly Payment
                                </Type.BodyFour>
                                <Type.DisplayFour className="sprk-u-man" id={`monthly-payment-${id}`} style={{ fontWeight: 'normal' }}>
                                    {formatMoney(monthlyPayment)}
                                </Type.DisplayFour>
                            </Stack.Item>
                            <Stack.Item style={{ flex: 1 }}>
                                <Type.BodyFour
                                    className="antialiased sprk-u-Color--black-tint-75 sprk-u-WhiteSpace--nowrap">
                                    Term
                                </Type.BodyFour>
                                <Type.DisplayFour
                                    id={`term-${id}`}
                                    className="sprk-u-WhiteSpace--nowrap sprk-u-man"
                                    style={{ fontWeight: 'normal' }}>
                                    {duration} Months
                                </Type.DisplayFour>
                            </Stack.Item>
                        </Stack>
                    </Stack.Item>

                    <Stack.Item className="sprk-u-man" style={{ flex: 3 }}>
                        <Stack className={`sprk-u-Flex--row ${isOnlyOffer ? 'sprk-u-Flex--col@s sprk-u-Flex--row@m' : ''
                            }`} style={{ gap: '12px' }}>
                            <Stack.Item style={{ flex: 1 }}>
                                <Type.BodyFour
                                    className="antialiased sprk-u-Color--black-tint-75 sprk-u-WhiteSpace--nowrap">
                                    <Tooltip
                                        className="black"
                                        id={`offer-apr-tooltip-${id}`}
                                        text={aprTooltip}>
                                        APR
                                        <QuestionMarkIcon style={{
                                            height: '1em',
                                            marginLeft: '4px',
                                            marginTop: '-2px',
                                        }} />
                                    </Tooltip>
                                </Type.BodyFour>
                                <Type.DisplaySix id={`annual-percentage-rate-${id}`} className={`sprk-u-Color--black-tint-75 sprk-u-pts sprk-u-man ${rateAprAdditionalClass}`}>
                                    <FormattedNumber
                                        value={annualPercentageRate / 100}
                                        maximumFractionDigits={3}
                                        // eslint-disable-next-line react/style-prop-object
                                        style="percent" />
                                </Type.DisplaySix>
                            </Stack.Item>
                            <Stack.Item style={{ flex: 1 }}>
                                <Type.BodyFour
                                    className="antialiased sprk-u-Color--black-tint-75 sprk-u-WhiteSpace--nowrap">
                                    <Tooltip
                                        className="black"
                                        id={`offer-interest-rate-tooltip-${id}`}
                                        // eslint-disable-next-line max-len
                                        text={`This rate is built into your monthly payment amount and stays the same
                                            through the life of your loan. ${rateAdditionalTooltip}`}
                                    >
                                        Interest Rate
                                        <QuestionMarkIcon style={{
                                            height: '1em',
                                            marginLeft: '4px',
                                            marginTop: '-2px',
                                        }} />
                                    </Tooltip>
                                </Type.BodyFour>
                                <Type.DisplaySix className={`sprk-u-Color--black-tint-75 sprk-u-pts sprk-u-man ${rateAprAdditionalClass}`}>
                                    <FormattedNumber
                                        id={`auto-pay-rate-${id}`}
                                        value={autoPayRate / 100}
                                        maximumFractionDigits={3}
                                        // eslint-disable-next-line react/style-prop-object
                                        style="percent" />
                                </Type.DisplaySix>
                            </Stack.Item>
                        </Stack>
                    </Stack.Item>

                    <Stack.Item className='sprk-u-man' style={{ flex: 4 }}>
                        <Stack className={`sprk-u-Flex--row ${isOnlyOffer ? 'sprk-u-Flex--col@s sprk-u-Flex--row@m' : ''
                            }`} style={{ gap: '12px' }}>
                            <Stack.Item style={{ flex: 1 }}>
                                <Type.BodyFour
                                    className="antialiased sprk-u-Color--black-tint-75 sprk-u-WhiteSpace--nowrap">
                                    <Tooltip
                                        className="black"
                                        id={`offer-origination-tooltip-${id}`}
                                        // eslint-disable-next-line max-len
                                        text={`An origination fee of ${originationFeeRate}% will be deducted from the proceeds of the loan`}
                                    >
                                        Origination Fee
                                        <QuestionMarkIcon style={{
                                            height: '1em',
                                            marginLeft: '4px',
                                            marginTop: '-2px',
                                        }} />
                                    </Tooltip>
                                </Type.BodyFour>
                                <Type.DisplaySix className="sprk-u-Color--black-tint-75 sprk-u-pts sprk-u-man" id={`origination-fee-${id}`} >
                                    {formatMoney(originationFee, true)}
                                </Type.DisplaySix>
                            </Stack.Item>
                            <Stack.Item style={{ flex: 1 }}>
                                <Type.BodyFour
                                    className="antialiased sprk-u-Color--black-tint-75 sprk-u-WhiteSpace--nowrap">
                                    <Tooltip
                                        className="black"
                                        id={`offer-deposit-tooltip-${id}`}
                                        // eslint-disable-next-line max-len
                                        text="Money in Account shows you what will be deposited in your
                                            bank account after the loan origination fee has been deducted
                                            from your total loan amount."
                                    >
                                        Money in Account
                                        <QuestionMarkIcon style={{
                                            height: '1em',
                                            marginLeft: '4px',
                                            marginTop: '-2px',
                                        }} />
                                    </Tooltip>
                                </Type.BodyFour>
                                <Type.DisplaySix className="sprk-u-Color--black-tint-75 sprk-u-pts sprk-u-man" id={`money-in-account-${id}`}>
                                    {formatMoney(amountFinanced, true)}
                                </Type.DisplaySix>
                            </Stack.Item>
                        </Stack>
                    </Stack.Item>

                    <Stack.Item className="sprk-u-pts sprk-u-Display--flex sprk-o-Stack--center-column">
                        <Button
                            id={`select-offer-${id}`}
                            name={`select-offer-button-${id}`}
                            onClick={() => selectOffer(offer.id)}
                            disabled={isSelectingOffer}
                            variant='secondary'
                            className='sprk-u-Width-100 sprk-u-WhiteSpace--nowrap
                                sprk-b-TypeBodyFour sprk-u-pvs sprk-u-phm'
                            style={{ appearance: 'none', borderWidth: '1px' }}>
                            Select and Continue
                        </Button>
                    </Stack.Item>
                </Stack>
            </Box>
        );
    }
}

export const OfferV2ListRow = connect(
    // State
    () => ({}),
    // Dispatch
    (dispatch) => ({
        selectOffer: (offerId) => dispatch((_, getState) => {
            const state = getState();
            if (state && state.loan && state.loan.loanJson && state.loan.loanJson.loanId) {
                dispatch(actionCreator.selectOfferAuthenticated(offerId, state.loan.loanJson.loanId));
            } else {
                dispatch(actionCreator.selectOffer(offerId))
            }
        }),
    })
)(OfferV2ListRowComponent);


class OffersV2ListComponent extends Component {
    static defaultProps = {
        descending: false,
        maxOffers: 10,
        hasQlDiscount: false
    }
    static propTypes = {
        groupedOffers: offersSchemaGroups,
        isSelectingOffer: PropTypes.bool.isRequired,
        descending: PropTypes.bool,
        maxOffers: PropTypes.number,
        hasQlDiscount: PropTypes.bool,
        qlDiscountAmount: PropTypes.string,
        autoPayDiscountConfig: PropTypes.object
    };

    render() {
        // Get grouped offers
        const { groupedOffers, isSelectingOffer, descending, hasQlDiscount, qlDiscountAmount, autoPayDiscountConfig } = this.props;
        const amounts = (descending ? sortNumbersDesc : sortNumbersAsc)(
            Object.keys(groupedOffers)
        );

        return (
            <Fragment>
                {amounts.map((amount) => {
                    const offerGroup = groupedOffers[amount];
                    const durations = Object.keys(offerGroup);

                    return (
                        <Box key={`offer-group-${amount}`} padding="flush" className="sprk-u-ptl">
                            <Card
                                className="sprk-u-MaxWidth--none sprk-u-BackgroundColor--white"
                                style={{ overflow: 'hidden' }}>
                                <Box className="sprk-u-BackgroundColor--purple text-center sprk-u-pvs">
                                    <Type.BodyFour className="sprk-u-man sprk-u-Color--white">
                                        Loan Amount
                                    </Type.BodyFour>
                                    <Type.DisplayFour
                                        className="sprk-u-pbs sprk-u-man sprk-u-Color--white"
                                        style={{ fontWeight: 'normal' }}>
                                        {formatMoney(Number(amount))}
                                    </Type.DisplayFour>
                                </Box>

                                {hasQlDiscount && (
                                    <div style={{ margin: '0 auto' }}>
                                        <div className="discount-icon-text-container" style={{ paddingTop: '4px'}}>
                                            <StarIcon width={24} height={24} fill="currentColor" className="discount-icon" />
                                            <p className="sprk-u-pls sprk-b-TypeBodyFour">
                                                Discounts applied
                                            </p>
                                        </div>
                                    </div>
                                )}

                                <Stack className="sprk-u-Flex--col sprk-u-Flex--row@s sprk-u-Flex--col@m">
                                    {sortNumbersAsc(durations).map((key, index) => (
                                        <Fragment key={`offer-${amount}-${key}`}>
                                            {index > 0 && (
                                                // Divider
                                                <div
                                                    className='sprk-u-Display--flex sprk-u-Flex--col sprk-u-Flex--row@s
                                                        sprk-u-Flex--col@m sprk-u-BackgroundColor--purple'
                                                    style={{ justifyContent: 'center' }}>
                                                    <div style={{ width: '2px', height: '2px' }} />
                                                </div>
                                            )}
                                            <OfferV2ListRow
                                                offer={offerGroup[key]}
                                                numberOfTerms={durations.length}
                                                isSelectingOffer={isSelectingOffer}
                                                hasQlDiscount={hasQlDiscount}
                                                qlDiscountAmount={qlDiscountAmount}
                                                autoPayDiscountConfig={autoPayDiscountConfig}
                                            />
                                        </Fragment>
                                    ))}
                                </Stack>
                            </Card>
                        </Box>
                    );
                })}
            </Fragment>
        );
    }
}

const mapStateToProps = (state, props) => {
    const offerSelecting = offerStateSelectors.getOfferSelecting(state);
    return {
        ...props,
        isSelectingOffer: offerSelecting ? offerSelecting : false,
        groupedOffers: stateSelectors.selectGroupedOffers(state, props)
    };
};

export const OffersV2List = connect(
    // State
    mapStateToProps,
    // Dispatch
    () => ({}),
)(OffersV2ListComponent);
